import type { AxiosInstance } from 'axios'
import { getEnv } from '@shared/config/env'
import axios from 'axios'

export const AUTH_HEADER = 'Authorization'

let handle401Error: (error: unknown) => void

// Add global response interceptor for 401 errors

export function set401ErrorHandler(handler: (error: unknown) => void) {
  handle401Error = handler
}

export function emitLogoutEvent() {
  handle401Error(new Error('User logged out'))
}

/**
 * Token getter injected at app init time.
 * Decouples the API client from Pinia — the host app binds it once via
 * `setAuthTokenGetter(() => authStore.token)` after creating the store.
 */
let tokenGetter: (() => string | undefined) | null = null

export function setAuthTokenGetter(getter: () => string | undefined) {
  tokenGetter = getter
}

let api: AxiosInstance | undefined

export function createApiClient() {
  const newApi = axios.create({
    baseURL: getEnv().APP_API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  newApi.interceptors.request.use((config) => {
    const token = tokenGetter?.()
    if (token)
      config.headers[AUTH_HEADER] = `Bearer ${token}`
    return config
  })

  newApi.interceptors.response.use(
    response => response,
    (error) => {
      if (error.response?.status === 401) {
        handle401Error(error)
      }
      return Promise.reject(error)
    },
  )

  return newApi
}

function getApi() {
  if (!api) {
    api = createApiClient()
  }
  return api
}

export default getApi
